// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.
import { createSelector } from 'reselect'

import { sortBy } from 'lodash'
import { VERY_OLD_DATE, getDateValue, isDeviceFileURL } from '../helpers/file'
import { fullSystemStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').KnownFilesEntry} KnownFilesEntry
 * @typedef {import('../index').FileURL} FileURL
 */

/**
 * @type {function(PlottrState): Array<KnownFilesEntry>}
 */
export const knownFilesSelector = createSelector(
  fullSystemStateSelector,
  ({ knownFiles }) => knownFiles ?? []
)
const selectURL = (_state, url) => url
/**
 * @type {function(PlottrState, FileURL): (KnownFilesEntry | Null)}
 */
export const fileFromFileURLSelector = createSelector(
  knownFilesSelector,
  selectURL,
  (fileList, searchFileURL) => {
    return fileList.find(({ fileURL }) => fileURL === searchFileURL) ?? null
  }
)
/**
 * @type {function(PlottrState): Array<KnownFilesEntry>}
 */
export const fileSystemKnownFilesSelector = createSelector(knownFilesSelector, (files) => {
  return files.filter(({ fileURL }) => {
    return isDeviceFileURL(fileURL)
  })
})
/**
 * @type {function(PlottrState): Array<KnownFilesEntry>}
 */
export const cloudFileListSelector = createSelector(knownFilesSelector, (files) => {
  return files.filter(({ isCloudFile }) => isCloudFile)
})
/**
 * @param {PlottrState} _state
 * @param {String} searchTerm
 * @return {String}
 */
const searchTermSelector = (_state, searchTerm) => searchTerm
/**
 * @type {function(PlottrState, String): [Array<String>, Record<String, KnownFilesEntry>]}
 */
export const sortedFileSystemKnownFilesByIdSelector = createSelector(
  fileSystemKnownFilesSelector,
  searchTermSelector,
  (files, searchTerm) => {
    const filteredFileIds = files
      .filter((f) => {
        if (searchTerm && searchTerm.length > 1) {
          return !!(f.fileName || f.path)?.toLowerCase()?.includes?.(searchTerm.toLowerCase())
        } else {
          return true
        }
      })
      .reduce((acc, { id }) => {
        if (id) {
          return [...acc, id]
        } else {
          return acc
        }
      }, [])
    const sortedIds = sortBy(filteredFileIds, (id) => {
      const correspondingFile = files.find((file) => file.id === id)
      if (correspondingFile) {
        return getDateValue(correspondingFile)
      } else {
        return VERY_OLD_DATE
      }
    }).reverse()
    const filesById = files.reduce((acc, nextFile) => {
      if (nextFile.id) {
        return {
          ...acc,
          [nextFile.id]: nextFile,
        }
      } else {
        return acc
      }
    }, {})
    return [sortedIds, filesById]
  }
)

/**
 * @param {PlottrState} _state
 * @param {String} fileURL
 * @return {String}
 */
const fileURLSelector = (_state, fileURL) => {
  return fileURL
}

/**
 * @type {function(PlottrState, FileURL): boolean}
 */
export const isTempFileSelector = createSelector(
  fileSystemKnownFilesSelector,
  fileURLSelector,
  (files, fileURL) => {
    const file = files.find((file) => {
      return file.fileURL === fileURL
    })
    return !!(file && file.isTempFile)
  }
)

/**
 * @type {function(PlottrState, String): [Array<String>, Record<String,KnownFilesEntry>]}
 */
export const sortedProKnownFilesByIdSelector = createSelector(
  cloudFileListSelector,
  searchTermSelector,
  (files, searchTerm) => {
    const filteredFileIds = files
      .filter((f) => {
        if (searchTerm && searchTerm.length > 1) {
          return (f.fileName || f.path || '').toLowerCase().includes(searchTerm.toLowerCase())
        } else {
          return true
        }
      })
      .map(({ id }) => {
        return id
      })
      .reduce((acc, id) => {
        if (id) {
          return [...acc, id]
        } else {
          return acc
        }
      }, [])
    const sortedIds = sortBy(filteredFileIds, (id) => {
      const file = files.find((f) => f.id === id)
      if (file) {
        return getDateValue(file)
      } else {
        return VERY_OLD_DATE
      }
    }).reverse()
    const filesById = files.reduce((acc, nextFile) => {
      if (nextFile.id) {
        return {
          ...acc,
          [nextFile.id]: nextFile,
        }
      } else {
        return acc
      }
    }, {})
    return [sortedIds, filesById]
  }
)

/**
 * @type {function(PlottrState, String): Array<KnownFilesEntry>}
 */
export const sortedFlatProKnownFilesByIdSelector = createSelector(
  sortedProKnownFilesByIdSelector,
  (proFiles) => {
    return proFiles[0].map((id) => proFiles[1][id])
  }
)
